<!--
  SuccessScreen — the full-screen success state that replaces the chat once the
  account is created. One-shot confetti throw behind the XL character (the success visual
  of the welcome character) + path-aware title/incentive/CTAs (resolved by the orchestrator
  via resolveSuccess). Emits `primary` / `secondary` for the redirect handlers.
-->
<template>
  <div class="success-screen">
    <ConfettiCanvas class="success-confetti" />
    <img :src="characterSrc" class="success-char" :alt="characterName" />
    <h2 class="success-title">{{ config.intro }}</h2>
    <p class="success-incentive">{{ config.incentive }}</p>
    <div class="success-actions">
      <button type="button" class="cta cta-blue" @click="$emit('primary')">{{ config.primaryCta }}</button>
      <button
        v-if="config.secondary && config.secondary.kind === 'button'"
        type="button"
        class="auth-btn auth-email"
        @click="$emit('secondary')"
      >
        {{ config.secondary.label }}
      </button>
      <button
        v-else-if="config.secondary && config.secondary.kind === 'link'"
        type="button"
        class="cta-secondary"
        @click="$emit('secondary')"
      >
        {{ config.secondary.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SuccessConfig } from '../config/success-wordings'
import { ConfettiCanvas } from '@twg/ui'

defineProps<{
  config: SuccessConfig
  characterSrc: string
  characterName: string
}>()

defineEmits<{ primary: []; secondary: [] }>()
</script>

<style scoped>
.success-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  padding: 32px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: fadeUp 0.35s both;
}
.success-confetti {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.success-char,
.success-title,
.success-incentive,
.success-actions {
  position: relative;
  z-index: 1;
}
.success-char {
  width: 180px;
  max-width: 50%;
  margin-bottom: 24px;
  animation: successPop 0.5s cubic-bezier(0.2, 0.8, 0.3, 1.15) both;
}
.success-title {
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--navy-800);
  line-height: 1.25;
  margin: 0 0 12px;
}
.success-incentive {
  font-size: 15px;
  color: #4a5860; /* WCAG-AAA legal-text contrast — preserved verbatim (INSTALL §5) */
  line-height: 1.5;
  margin: 0 0 32px;
  max-width: 360px;
}
.success-actions {
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
@keyframes successPop {
  0% {
    opacity: 0;
    transform: scale(0.85);
  }
  60% {
    transform: scale(1.04);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
@media (min-width: 640px) {
  .success-char {
    width: 220px;
  }
  .success-title {
    font-size: 28px;
  }
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .success-char,
  .success-screen {
    animation: none;
  }
}
.cta {
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: var(--brand-default);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  line-height: 14px;
  transition: background 0.15s;
}
.cta:hover {
  background: var(--blue-800);
}
.auth-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.auth-email {
  background: var(--white);
  border: 1.5px solid var(--navy-800);
  color: var(--navy-800);
}
.auth-email:hover {
  background: var(--navy-800);
  color: var(--white);
}
.cta-secondary {
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  color: var(--grey-500);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s;
}
.cta-secondary:hover {
  color: var(--navy-800);
}
</style>
